/// <amd-module name="Dashboards/Directory/redux/DashboardsDirectorySaga" />
import { CreateDashboardPanel } from "Dashboards/DashboardPanel/CreateDashboardPanel";
import produce from "immer";
import * as React from "react";
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { ISecurityPanelProps } from "TFS/Security/Common/Security.Controls.Props";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { IUnifiedDirectoryDataProviderData } from "VSS/Features/ReduxDirectory/Contracts";
import { DirectoryActions, DirectoryActionTypes } from "VSS/Features/ReduxDirectory/Redux/DirectoryActions";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import * as DashboardsDirectoryConstants from "../DashboardsDirectoryConstants";
import {
    Dashboard,
    DashboardFavoriteWithOwner,
    DashboardsAndOwnersLists,
    DashboardWithOwner,
    DataProviderOwner,
    IDashboardsDirectoryArtifact,
    NewDashboardItem,
    Team
} from "../DashboardsDirectoryContracts";
import { DashboardsDirectoryActions, DashboardsDirectoryActionTypes } from "./DashboardsDirectoryActions";
import { getAllDirectoryData, getUnifiedDirectoryData } from "./DashboardsDirectorySelectors";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

/**
 * Listens for actions specific to managing dashboards on the directory page
 */
export function* dashboardsDirectorySaga(): SagaIterator {
    yield effects.takeEvery(DashboardsDirectoryActionTypes.OpenNewDashboardDialog, openNewDashboardExperience);
    yield effects.takeEvery(DashboardsDirectoryActionTypes.OpenDashboardSecurityDialog, openDashboardSecurityDialog);
    yield effects.takeEvery(DashboardsDirectoryActionTypes.OpenDeleteDashboardDialog, openDeleteDashboardDialog);
    yield effects.takeEvery(DashboardsDirectoryActionTypes.InitialDataLoaded, provideMyDirectoryData);
    yield effects.takeEvery(DashboardsDirectoryActionTypes.AllDataLoaded, provideAllDirectoryData);
    // Watch for the directory requesting data for the all group
    yield effects.takeEvery(DirectoryActionTypes.RequestAllData, getAllData);

    yield effects.call(getUserClaims);
    yield effects.call(initializeData);
}

export function* initializeData(): SagaIterator {
    const newDashboardDirectoryFeatureFlag = "WebAccess.Dashboards.NWPEnabled";
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;

    const ffEnabled = isFeatureFlagEnabled(pageContext, newDashboardDirectoryFeatureFlag, false);
    
    let hasMemberClaim: boolean = false;
    if(!ffEnabled){
         hasMemberClaim = (yield effects.call(userHasClaim, pageContext, UserClaims.Member)) as any;

        let myDashboards: Dashboard[] = (yield effects.call(
            [contributionService, contributionService.getDataAsync],
            DashboardsDirectoryConstants.myDashboardsDataProviderId
        )) as any;
        yield effects.put(DashboardsDirectoryActions.myDashboardsLoaded(myDashboards));
    }

    // This check is only for public non-member users who do not have access to team data.
    if (!ffEnabled && hasMemberClaim) {
        const myOwners: DataProviderOwner[] = (yield effects.call(
            [contributionService, contributionService.getDataAsync],
            DashboardsDirectoryConstants.myOwnersDataProviderId
        )) as any;
        yield effects.put(DashboardsDirectoryActions.myOwnersLoaded(myOwners.map(o => o.value)));
    }

    const favorites: DashboardFavoriteWithOwner[] = (yield effects.call(
        [contributionService, contributionService.getDataAsync],
        DashboardsDirectoryConstants.favoritesDataProviderId
    )) as any;
    yield effects.put(DashboardsDirectoryActions.myFavoritesLoaded(favorites));

    if(!ffEnabled){
        const mru: DashboardWithOwner = (yield effects.call(
            [contributionService, contributionService.getDataAsync],
            DashboardsDirectoryConstants.mruDashboardDataProviderId
        )) as any;
        yield effects.put(DashboardsDirectoryActions.mruDashboardLoaded(mru));
    }
    
    const allDashboardsAndTeams: DashboardsAndOwnersLists = (yield effects.call(
        [contributionService, contributionService.getDataAsync],
        DashboardsDirectoryConstants.allDashboardsDataProviderId
    )) as any;
    yield effects.put(DashboardsDirectoryActions.allDashboardsLoaded(allDashboardsAndTeams));
    yield effects.put(DashboardsDirectoryActions.allDataLoaded());

    yield effects.put(DashboardsDirectoryActions.initialDataLoaded());
}

export function* getUserClaims(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const hasMemberClaim: boolean = (yield effects.call(userHasClaim, pageContext, UserClaims.Member)) as any;
    yield effects.put(DashboardsDirectoryActions.setCanPerformDashboardActionsPermission(hasMemberClaim, hasMemberClaim, hasMemberClaim));
}

export function* getAllData(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;

    const allDashboardsAndTeams: DashboardsAndOwnersLists = (yield effects.call(
        [contributionService, contributionService.getDataAsync],
        DashboardsDirectoryConstants.allDashboardsDataProviderId
    )) as any;
    yield effects.put(DashboardsDirectoryActions.allDashboardsLoaded(allDashboardsAndTeams));
    yield effects.put(DashboardsDirectoryActions.allDataLoaded());
}

export function* provideMyDirectoryData(): SagaIterator {
    const unifiedDirectoryData: IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact> = (yield effects.select(
        getUnifiedDirectoryData
    )) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const project: IProjectData = (yield effects.select(currentProject)) as any;

    const unifiedDirectoryDataWithUrls = (yield effects.call(setArtifactUrls, pageContext, project, unifiedDirectoryData)) as any;
    yield effects.put(DirectoryActions.initialDataLoaded(unifiedDirectoryDataWithUrls));
}

export function* provideAllDirectoryData(): SagaIterator {
    const allDirectoryData: IDashboardsDirectoryArtifact[] = (yield effects.select(getAllDirectoryData)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const project: IProjectData = (yield effects.select(currentProject)) as any;

    const allDirectoryDataWithUrls = (yield effects.call<
        IDashboardsDirectoryArtifact[] | IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact>,
        IVssPageContext,
        IProjectData,
        IDashboardsDirectoryArtifact[]
    >(setArtifactUrls, pageContext, project, allDirectoryData)) as any;
    yield effects.put(DirectoryActions.receiveAllData({ artifacts: allDirectoryDataWithUrls }));
}

function setArtifactUrls(
    pageContext: IVssPageContext,
    project: IProjectData,
    dashboards: IDashboardsDirectoryArtifact[]
): IDashboardsDirectoryArtifact[];
function setArtifactUrls(
    pageContext: IVssPageContext,
    project: IProjectData,
    directoryData: IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact>
): IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact>;
function setArtifactUrls(
    pageContext: IVssPageContext,
    project: IProjectData,
    data: IDashboardsDirectoryArtifact[] | IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact>
): IDashboardsDirectoryArtifact[] | IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact> {
    return produce(data, draft => {
        const artifacts = Array.isArray(draft) ? draft : draft.artifacts;
        for (const dashboard of artifacts) {
            if (project) {
                dashboard.url = getDashboardUrl(
                    pageContext,
                    project.name,
                    dashboard.owners.length ? dashboard.owners[0].name : undefined,
                    dashboard.id
                );
            }
        }
    });
}

function* getLayoutManager() {
    // @ts-expect-error TypeScript 4.2 upgrade
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    // @ts-expect-error TypeScript 4.2 upgrade
    const layoutManager: IVssLayoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    return layoutManager;
}

/**
 * Saga which is executed when the New Dashboard button on the directory is clicked
 */
function* openNewDashboardExperience(): SagaIterator {
    const layoutManager: IVssLayoutManager = (yield effects.call(getLayoutManager)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    let newDashboard: NewDashboardItem;

    const teamAgnosticDashboardsEnabled = (yield effects.call(
        isFeatureEnabled,
        pageContext,
        "ms.vss-dashboards.new-dashboard-management-experience-feature",
        false
    )) as any;
    if (teamAgnosticDashboardsEnabled) {
        const project: IProjectData = (yield effects.select(currentProject)) as any;
        const tfsTeamService: ITfsTeamService = (yield effects.call([pageContext, pageContext.getService], "ITfsTeamService")) as any;
        const [userTeams, allTeams] = (yield effects.all([
            effects.call([tfsTeamService, tfsTeamService.getMyTeams], project.id),
            effects.call([tfsTeamService, tfsTeamService.getAllTeams], project.id)
        ])) as any;

        newDashboard = (yield effects.call(showCreateDashboardPanel, layoutManager, project, userTeams, allTeams, pageContext)) as any;
    } else {
        newDashboard = (yield effects.call(renderLegacyCallout, layoutManager, getNewDashboardDialogProps)) as any;
        publishOnCreateDashboardSuccess(newDashboard, pageContext);
    }

    if (newDashboard != null) {
        const url: string = (yield effects.call(getDashboardUrl, pageContext, newDashboard.projectId, newDashboard.teamId, newDashboard.id, {
            ["isNew"]: "true"
        })) as any;
        yield effects.put(DirectoryActions.navigateToUrl(url));
    }
}

export function getDashboardUrl(
    pageContext: IVssPageContext,
    projectName: string,
    teamName: string | undefined,
    dashboardId: string,
    queryParams?: { [param: string]: string }
): string {
    const locationService: IVssLocationService = pageContext.getService("IVssLocationService");

    const routeValues: { [key: string]: string } = {
        project: projectName,
        id: dashboardId
    };

    if (teamName) {
        routeValues["team"] = teamName;
    }

    return locationService.routeUrl("ms.vss-dashboards-web.dashboards-new-experience-route", routeValues);
}

/**
 * Saga which is executed when the security context menu option is chosen for a dashboard on the directory
 */
function* openDashboardSecurityDialog(
    action: ActionsOfType<DashboardsDirectoryActions, DashboardsDirectoryActionTypes.OpenDashboardSecurityDialog>
): SagaIterator {
    const layoutManager: IVssLayoutManager = (yield effects.call(getLayoutManager)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const { dashboardId, dashboardName, teamId } = action.payload;
    const getProps = function(onDismiss: () => void, resolve: (value?: undefined) => void) {
        return getDashboardSecurityDialogProps(dashboardId, dashboardName, teamId, onDismiss, resolve, pageContext);
    };

    yield effects.call(renderLegacyCallout, layoutManager, getProps);
}

function* openDeleteDashboardDialog(
    action: ActionsOfType<DashboardsDirectoryActions, DashboardsDirectoryActionTypes.OpenDeleteDashboardDialog>
): SagaIterator {
    const layoutManager: IVssLayoutManager = (yield effects.call(getLayoutManager)) as any;

    const { dashboardId, teamId, projectId } = action.payload;
    const getProps = function(onDismiss: () => void, resolve: (dashboardWasDeleted: boolean) => void) {
        return getDeleteDashboardDialogProps(dashboardId, teamId, projectId, onDismiss, resolve);
    };

    const dashboardWasDeleted = (yield effects.call(renderLegacyCallout, layoutManager, getProps)) as any;
    if (dashboardWasDeleted) {
        yield effects.put(DirectoryActions.removeArtifact(dashboardId));

        /* Publish telemetry when dashboard is deleted successfully */
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        yield effects.call(publishTelemetryOnDashboardDeleteSuccess, dashboardId, pageContext);
    }
}

function getNewDashboardDialogProps(onDismiss: () => void, resolve: (value?: NewDashboardItem) => void): ILegacyComponentProperties {
    return {
        modules: ["Dashboards/Components/Shared/CreateDashboardDialog/CreateDashboardDialog"],
        wrappedType: "CreateDashboardDialog",
        allowTeamSelection: true,
        onDashboardCreated: (newDashboard: NewDashboardItem) => {
            onDismiss();
            resolve(newDashboard);
        },
        onDismiss: () => {
            onDismiss();
            resolve();
        }
    };
}

function getDashboardSecurityDialogProps(
    dashboardId: string,
    dashboardName: string,
    teamId: string,
    onDismiss: () => void,
    resolve: () => void,
    pageContext: IVssPageContext
): ILegacyComponentProperties {
    if (isFeatureFlagEnabled(pageContext, DashboardsDirectoryConstants.WebAccessDashboardsSecurityDialog, false)) {
        const wrappedProps: ISecurityPanelProps = {
            onCancel: () => {
                onDismiss();
                resolve();
            },
            securityViewTitle: dashboardName,
            permissionSetId: DashboardsDirectoryConstants.SecurityNameSpaceIdForDashboards,
            permissionSetToken: JSON.stringify({
                teamId: teamId,
                dashboardId: dashboardId
            })
        };

        return {
            wrappedType: "ms.vss-admin-web.security-panel-component",
            dependencies: ["ms.vss-admin-web.security-view-common-content"],
            wrappedProps: wrappedProps
        };
    } else {
        return {
            modules: ["Dashboards/Components/Directory/DashboardsSecurityDialog"],
            wrappedType: "DashboardSecurityDialog",
            dashboardId: dashboardId,
            dashboardName: dashboardName,
            teamId: teamId,
            onClose: () => {
                onDismiss();
                resolve();
            }
        };
    }
}

function getDeleteDashboardDialogProps(
    dashboardId: string,
    teamId: string | undefined,
    projectId: string,
    layoutManagerOnDismiss: () => void,
    resolve: (dashboardWasDeleted: boolean) => void
) {
    let dashboardWasDeleted = false;

    const onDismiss = () => {
        layoutManagerOnDismiss();
        resolve(dashboardWasDeleted);
    };

    const onDelete = () => {
        dashboardWasDeleted = true;
    };

    return {
        modules: ["Dashboards/Components/Directory/DeleteDashboardDialog"],
        wrappedType: "DeleteDashboardDialog",
        dashboardId,
        teamId,
        projectId,
        onClose: onDismiss,
        onDelete: onDelete
    };
}

function renderLegacyCallout<T>(
    layoutManager: IVssLayoutManager,
    getProps: (
        onDismiss: () => void,
        resolve: (value: T | PromiseLike<T> | undefined) => void,
        reject: (reason?: any) => void
    ) => ILegacyComponentProperties
): PromiseLike<T> {
    const promise = new Promise<T>((resolve, reject) =>
        layoutManager.renderCallout((onDismiss: () => void) => React.createElement(LegacyComponent, getProps(onDismiss, resolve, reject)))
    );

    return promise;
}

function showCreateDashboardPanel(
    layoutManager: IVssLayoutManager,
    project: IProjectData,
    userTeams: Team[],
    allTeams: Team[],
    pageContext: IVssPageContext
): Promise<NewDashboardItem | undefined> {
    return new Promise((resolve, reject) => {
        layoutManager.renderCallout((onDismiss: () => void) => {
            return React.createElement(CreateDashboardPanel, {
                onDismiss: () => {
                    onDismiss();
                    resolve(undefined);
                },
                onDashboardCreated: (newDashboard: NewDashboardItem) => {
                    onDismiss();
                    resolve(newDashboard);
                    publishOnCreateDashboardSuccess(newDashboard, pageContext)
                },
                project,
                userTeams,
                allTeams
            });
        });
    });
}

function publishOnCreateDashboardSuccess(newDashboard: NewDashboardItem, pageContext: IVssPageContext): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent("Dashboards", "Dashboards.Create", {
        Scenario: "DashboardDirectory.CreateDashboardSuccess",
        NewDashboardId: newDashboard.id
    })
}

function publishTelemetryOnDashboardDeleteSuccess(deletedDashboardId: string, pageContext: IVssPageContext): void{
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent("Dashboards", "Dashboards.Delete", {
        Scenario: "DashboardDirectory.DeleteDashboardSuccess",
        DeletedDashboardId: deletedDashboardId
    })
}